import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/DeliveryStartAdmin.css';

interface DriverStatus {
  driver_id: number;
  username: string;
  name: string;
  assigned_apartments: string[];
  confirmed: boolean;
  confirmation_time: string | null;
  delivery_start_time?: string;
  delivery_start_day_offset?: number;
  delivery_start_day_label?: string;
  delivery_start_window_label?: string;
  delivery_start_end_time?: string;
  can_start_now?: boolean;
  unavailable_reason?: string;
  is_closed?: boolean;
  has_delivery?: boolean;
  delivery_apartments?: string[];
  delivery_apartment_count?: number;
  delivery_total_count?: number;
  seconds_until_start?: number;
  seconds_until_end?: number;
  server_now?: string;
  start_available_at?: string;
  start_closed_at?: string;
  include_in_notification?: boolean;
}

interface AllStatus {
  date: string;
  total_drivers: number;
  confirmed_count: number;
  unconfirmed_count: number;
  drivers: DriverStatus[];
}

interface NotificationSettings {
  check_time: string;
  repeat_check_enabled: boolean;
  repeat_interval_minutes: number;
  repeat_end_time: string;
  admin_phone: string | null;
  pushover_user_key: string | null;
  pushover_enabled: boolean;
}

const DeliveryStartAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<AllStatus | null>(null);
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  
  // 사진 설정
  const [showPhotoSettings, setShowPhotoSettings] = useState(false);
  const [photoGlobalRequired, setPhotoGlobalRequired] = useState(false);
  const [photoRetentionDays, setPhotoRetentionDays] = useState(5);
  const [photoRequiredApartments, setPhotoRequiredApartments] = useState<{apartment_name: string; photo_required: boolean}[]>([]);
  const [allApartmentNames, setAllApartmentNames] = useState<string[]>([]);
  const [photoSettingsLoading, setPhotoSettingsLoading] = useState(false);
  const [apartmentNamesLoading, setApartmentNamesLoading] = useState(false);
  const [assignmentEditingDriverId, setAssignmentEditingDriverId] = useState<number | null>(null);
  const [assignmentSelections, setAssignmentSelections] = useState<Record<number, string[]>>({});
  const [assignmentSavingDriverId, setAssignmentSavingDriverId] = useState<number | null>(null);

  // 날짜 선택 (기본값: 내일 - 배송은 다음날 새벽)
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const [selectedDate, setSelectedDate] = useState(getTomorrowDate());

  // 설정 입력
  const [checkTime, setCheckTime] = useState('02:10');
  const [repeatEnabled, setRepeatEnabled] = useState(true);
  const [repeatInterval, setRepeatInterval] = useState(10);
  const [repeatEndTime, setRepeatEndTime] = useState('05:00');
  const [adminPhone, setAdminPhone] = useState('');
  const [pushoverKey, setPushoverKey] = useState('');

  const getApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    if (window.location.protocol === 'https:') {
      return window.location.origin;
    }
    return `http://${hostname}:8000`;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchStatus(token, selectedDate);
    fetchSettings(token);

    // 30초마다 자동 새로고침
    const interval = setInterval(() => fetchStatus(token, selectedDate), 30000);
    return () => clearInterval(interval);
  }, [navigate, selectedDate]);

  const fetchStatus = async (token: string, date?: string) => {
    try {
      const url = date 
        ? `${getApiBase()}/api/admin/delivery-start/all-status?target_date=${date}`
        : `${getApiBase()}/api/admin/delivery-start/all-status`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('출근 현황 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async (token: string) => {
    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/notification-settings`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setCheckTime(data.check_time || '02:10');
        setRepeatEnabled(data.repeat_check_enabled !== false);
        setRepeatInterval(data.repeat_interval_minutes || 10);
        setRepeatEndTime(data.repeat_end_time || '05:00');
        setAdminPhone(data.admin_phone || '');
        setPushoverKey(data.pushover_user_key || '');
      }
    } catch (err) {
      console.error('알림 설정 조회 실패:', err);
    }
  };

  const saveSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const formData = new FormData();
      formData.append('check_time', checkTime);
      formData.append('repeat_check_enabled', repeatEnabled.toString());
      formData.append('repeat_interval_minutes', repeatInterval.toString());
      formData.append('repeat_end_time', repeatEndTime);
      formData.append('admin_phone', adminPhone);
      formData.append('pushover_user_key', pushoverKey);
      formData.append('pushover_enabled', 'true');

      const response = await fetch(
        `${getApiBase()}/api/admin/notification-settings`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (response.ok) {
        alert('✅ 설정이 저장되었습니다!');
        setShowSettings(false);
        fetchSettings(token);
      } else {
        alert('❌ 설정 저장에 실패했습니다.');
      }
    } catch (err) {
      console.error('설정 저장 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  const toggleNotification = async (driverId: number, include: boolean) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const formData = new FormData();
      formData.append('include', include.toString());

      const response = await fetch(
        `${getApiBase()}/api/admin/toggle-driver-notification/${driverId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (response.ok) {
        // 성공 시 상태 새로고침
        fetchStatus(token, selectedDate);
      } else {
        alert('❌ 설정 변경에 실패했습니다.');
      }
    } catch (err) {
      console.error('알림 설정 변경 실패:', err);
    }
  };

  const updateStartWindow = async (driverId: number, startDayOffset: number, startTime: string, endTime: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const formData = new FormData();
      formData.append('start_time', startTime);
      formData.append('start_day_offset', startDayOffset.toString());
      formData.append('end_time', endTime);

      const response = await fetch(
        `${getApiBase()}/api/admin/update-driver-start-time/${driverId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (response.ok) {
        // 성공 시 상태 새로고침
        fetchStatus(token, selectedDate);
      } else {
        alert('❌ 시간 설정에 실패했습니다.');
      }
    } catch (err) {
      console.error('배송 시작 시간대 설정 실패:', err);
    }
  };

  const sendTestNotification = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!settings?.pushover_user_key) {
      alert('Pushover 사용자 키를 먼저 설정해주세요.');
      return;
    }

    if (!window.confirm('테스트 알림을 전송하시겠습니까?\n(Pushover 앱으로 알림이 전송됩니다)')) {
      return;
    }

    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/check-unconfirmed`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        alert('✅ 테스트 알림이 전송되었습니다!\nPushover 앱을 확인해주세요.');
      } else {
        alert('❌ 알림 전송에 실패했습니다.');
      }
    } catch (err) {
      console.error('테스트 알림 전송 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  const resetConfirmation = async (driverId: number, driverName: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!window.confirm(`${driverName}님의 배송 시작 확인을 초기화하시겠습니까?\n(${selectedDate} 날짜의 확인 기록이 삭제됩니다)`)) {
      return;
    }

    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/delivery-start/reset/${driverId}?target_date=${selectedDate}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        alert('✅ 초기화되었습니다!');
        fetchStatus(token, selectedDate);
      } else {
        const data = await response.json();
        alert(`❌ 초기화에 실패했습니다.\n${data.detail || ''}`);
      }
    } catch (err) {
      console.error('초기화 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  const forceConfirmStart = async (driverId: number, driverName: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!window.confirm(`${driverName}님의 배송 시작을 강제로 처리하시겠습니까?\n(${selectedDate} 날짜의 확인 기록이 생성됩니다)`)) {
      return;
    }

    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/delivery-start/force-confirm/${driverId}?target_date=${selectedDate}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        alert('강제 배송 시작 처리가 완료되었습니다.');
        fetchStatus(token, selectedDate);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`처리에 실패했습니다.\n${data.detail || ''}`);
      }
    } catch (err) {
      console.error('강제 배송 시작 처리 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  // ==================== 사진 설정 ====================
  const fetchPhotoSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setPhotoSettingsLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/api/photo-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPhotoGlobalRequired(data.photo_required || false);
        setPhotoRetentionDays(data.retention_days || 5);
        setPhotoRequiredApartments(data.required_apartments || []);
      }
    } catch (err) {
      console.error('사진 설정 로드 실패:', err);
    } finally {
      setPhotoSettingsLoading(false);
    }
  };

  const fetchAllApartmentNames = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setApartmentNamesLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/api/admin/apartment-configs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const names = (Array.isArray(data) ? data : [])
          .map((a: any) => String(a.apartment_name || '').trim())
          .filter(Boolean);
        if (names.length > 0) {
          setAllApartmentNames(Array.from(new Set(names)));
          return;
        }
      }
    } catch (err) {
      console.error('아파트 목록 조회 실패:', err);
    } finally {
      setApartmentNamesLoading(false);
    }

    // 아파트 설정 조회 실패 시 기사 담당 아파트에서 추출
    try {
      if (status?.drivers) {
        const aptSet = new Set<string>();
        status.drivers.forEach(d => d.assigned_apartments.forEach(a => aptSet.add(a)));
        setAllApartmentNames(Array.from(aptSet));
      }
    } finally {
      setApartmentNamesLoading(false);
    }
  };

  const savePhotoSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${getApiBase()}/api/photo-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          photo_required: photoGlobalRequired,
          retention_days: photoRetentionDays,
          required_apartments: photoRequiredApartments
        })
      });
      if (res.ok) {
        alert('✅ 사진 설정이 저장되었습니다.');
        setShowPhotoSettings(false);
      } else {
        alert('❌ 사진 설정 저장에 실패했습니다.');
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  const openPhotoSettings = () => {
    setShowPhotoSettings(true);
    fetchPhotoSettings();
    fetchAllApartmentNames();
  };

  const toggleApartmentPhotoRequired = (aptName: string) => {
    setPhotoRequiredApartments(prev => {
      const existing = prev.find(a => a.apartment_name === aptName);
      if (existing) {
        return prev.map(a => 
          a.apartment_name === aptName 
            ? { ...a, photo_required: !a.photo_required }
            : a
        );
      } else {
        return [...prev, { apartment_name: aptName, photo_required: true }];
      }
    });
  };

  const startAssignmentEdit = async (driver: DriverStatus) => {
    setAssignmentEditingDriverId(driver.driver_id);
    setAssignmentSelections(prev => ({
      ...prev,
      [driver.driver_id]: [...driver.assigned_apartments],
    }));
    if (allApartmentNames.length === 0) {
      await fetchAllApartmentNames();
    }
  };

  const cancelAssignmentEdit = (driverId: number) => {
    setAssignmentEditingDriverId(current => current === driverId ? null : current);
    setAssignmentSelections(prev => {
      const next = { ...prev };
      delete next[driverId];
      return next;
    });
  };

  const toggleDriverApartmentAssignment = (driverId: number, apartmentName: string) => {
    setAssignmentSelections(prev => {
      const current = prev[driverId] || [];
      const exists = current.includes(apartmentName);
      const nextSelection = exists
        ? current.filter(name => name !== apartmentName)
        : [...current, apartmentName];
      return {
        ...prev,
        [driverId]: nextSelection,
      };
    });
  };

  const saveDriverAssignments = async (driver: DriverStatus) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const selectedApartments = assignmentSelections[driver.driver_id] || [];
    if (!window.confirm(`${driver.name}님의 담당 아파트 ${selectedApartments.length}개를 저장하시겠습니까?`)) {
      return;
    }

    setAssignmentSavingDriverId(driver.driver_id);
    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/drivers/${driver.driver_id}/apartment-assignments`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            apartment_names: selectedApartments,
          }),
        }
      );

      if (response.ok) {
        setAssignmentEditingDriverId(null);
        setAssignmentSelections(prev => {
          const next = { ...prev };
          delete next[driver.driver_id];
          return next;
        });
        await fetchStatus(token, selectedDate);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`❌ 담당 아파트 저장에 실패했습니다.\n${data.detail || ''}`);
      }
    } catch (err) {
      console.error('담당 아파트 저장 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setAssignmentSavingDriverId(null);
    }
  };

  const getWindowStatusText = (driver: DriverStatus) => {
    const start = driver.delivery_start_time || '02:00';
    const end = driver.delivery_start_end_time || '10:00';
    const startDayOffset = Number(driver.delivery_start_day_offset || 0);
    const startLabel = startDayOffset === -1 ? '전날' : '당일';
    const windowLabel = `${startLabel} ${start}~당일 ${end}`;
    if (!driver.has_delivery) {
      return '해당 날짜 배송 없음';
    }
    if (driver.can_start_now) {
      return `현재 배송 시작 가능 (${windowLabel})`;
    }
    if (driver.unavailable_reason === 'closed' || driver.is_closed) {
      return `시작 마감 (당일 ${end} 이후)`;
    }
    return `시작 대기 중 (${startLabel} ${start} 이후)`;
  };

  if (loading) {
    return <div className="start-admin-container">
      <div className="loading">로딩 중...</div>
    </div>;
  }

  return (
    <div className="start-admin-container">
      <header className="admin-header">
        <h1>📊 배송 출근 확인 현황</h1>
        <div className="date-selector">
          <label>날짜 선택:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="date-input"
          />
        </div>
        <div className="header-actions">
          <button onClick={openPhotoSettings} className="photo-settings-btn">
            📷 사진 설정
          </button>
          <button onClick={() => setShowSettings(!showSettings)} className="settings-btn">
            ⚙️ 알림 설정
          </button>
          <button onClick={sendTestNotification} className="test-btn">
            🔔 테스트 알림
          </button>
          <button onClick={() => navigate('/admin')} className="back-btn">
            ← 돌아가기
          </button>
        </div>
      </header>

      {/* 알림 설정 모달 */}
      {showSettings && (
        <div className="settings-modal">
          <div className="modal-content">
            <h2>⚙️ 알림 설정</h2>
            
            <div className="form-group">
              <label>체크 시간 (새벽)</label>
              <input
                type="time"
                value={checkTime}
                onChange={(e) => setCheckTime(e.target.value)}
                className="time-input"
              />
              <p className="help-text">이 시간에 자동으로 미확인자를 체크합니다</p>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={repeatEnabled}
                  onChange={(e) => setRepeatEnabled(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                반복 알림 활성화
              </label>
              <p className="help-text">미확인자가 있으면 일정 간격으로 알림을 계속 보냅니다</p>
            </div>

            {repeatEnabled && (
              <>
                <div className="form-group">
                  <label>재확인 간격 (분)</label>
                  <input
                    type="number"
                    value={repeatInterval}
                    onChange={(e) => setRepeatInterval(parseInt(e.target.value) || 10)}
                    min="5"
                    max="60"
                    className="text-input"
                  />
                  <p className="help-text">5~60분 사이로 설정 (권장: 10분)</p>
                </div>

                <div className="form-group">
                  <label>반복 종료 시간</label>
                  <input
                    type="time"
                    value={repeatEndTime}
                    onChange={(e) => setRepeatEndTime(e.target.value)}
                    className="time-input"
                  />
                  <p className="help-text">이 시간 이후에는 알림을 보내지 않습니다</p>
                </div>
              </>
            )}

            <div className="form-group">
              <label>관리자 전화번호</label>
              <input
                type="tel"
                value={adminPhone}
                onChange={(e) => setAdminPhone(e.target.value)}
                placeholder="010-1234-5678"
                className="text-input"
              />
            </div>

            <div className="form-group">
              <label>Pushover 사용자 키</label>
              <input
                type="text"
                value={pushoverKey}
                onChange={(e) => setPushoverKey(e.target.value)}
                placeholder="uQiRzpo4DXghDmr9QzzfQu27cmVRsG"
                className="text-input"
              />
              <p className="help-text">
                Pushover 앱 → Settings → User Key 복사<br/>
                <a href="https://pushover.net" target="_blank" rel="noreferrer">pushover.net</a>에서 확인
              </p>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowSettings(false)} className="cancel-btn">
                취소
              </button>
              <button onClick={saveSettings} className="save-btn">
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 요약 정보 */}
      {status && (
        <div className="summary-cards">
          <div className="summary-card total">
            <div className="card-icon">👥</div>
            <div className="card-content">
              <div className="card-number">{status.total_drivers}</div>
              <div className="card-label">전체 배송기사</div>
            </div>
          </div>

          <div className="summary-card confirmed">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <div className="card-number">{status.confirmed_count}</div>
              <div className="card-label">출근 확인 완료</div>
            </div>
          </div>

          <div className="summary-card unconfirmed">
            <div className="card-icon">⚠️</div>
            <div className="card-content">
              <div className="card-number">{status.unconfirmed_count}</div>
              <div className="card-label">미확인</div>
            </div>
          </div>
        </div>
      )}

      {/* 배송기사 목록 */}
      <div className="drivers-list">
        <h2>배송기사 목록</h2>
        <p className="date-info">날짜: {status?.date}</p>

        <div className="drivers-grid">
          {status?.drivers.map(driver => (
            <div 
              key={driver.driver_id} 
              className={`driver-card ${driver.confirmed ? 'confirmed' : 'unconfirmed'}`}
            >
              <div className="driver-header">
                <div className="driver-info">
                  <h3>{driver.name}</h3>
                  <p className="driver-username">@{driver.username}</p>
                </div>
                <div className={`status-badge ${driver.confirmed ? 'confirmed' : 'unconfirmed'}`}>
                  {driver.confirmed ? '✅ 확인' : '⚠️ 미확인'}
                </div>
              </div>

              <div className="driver-details">
                <div className="driver-settings">
                  <label className="notification-toggle">
                    <input
                      type="checkbox"
                      checked={driver.include_in_notification !== false}
                      onChange={(e) => toggleNotification(driver.driver_id, e.target.checked)}
                    />
                    <span>알림 포함</span>
                  </label>
                  
                  <div className="start-time-setting">
                    <label>기준:</label>
                    <select
                      value={Number(driver.delivery_start_day_offset || 0)}
                      onChange={(e) => updateStartWindow(
                        driver.driver_id,
                        Number(e.target.value),
                        driver.delivery_start_time || '02:00',
                        driver.delivery_start_end_time || '10:00'
                      )}
                      className="day-offset-select"
                    >
                      <option value={0}>당일</option>
                      <option value={-1}>전날</option>
                    </select>
                    <label>시작:</label>
                    <input
                      type="time"
                      value={driver.delivery_start_time || '02:00'}
                      onChange={(e) => updateStartWindow(
                        driver.driver_id,
                        Number(driver.delivery_start_day_offset || 0),
                        e.target.value,
                        driver.delivery_start_end_time || '10:00'
                      )}
                      className="time-input-small"
                    />
                    <label>마감:</label>
                    <input
                      type="time"
                      value={driver.delivery_start_end_time || '10:00'}
                      onChange={(e) => updateStartWindow(
                        driver.driver_id,
                        Number(driver.delivery_start_day_offset || 0),
                        driver.delivery_start_time || '02:00',
                        e.target.value
                      )}
                      className="time-input-small"
                    />
                  </div>
                </div>

                {!driver.confirmed && (
                  <div className={`start-lock-admin ${!driver.has_delivery ? 'no-delivery' : ''} ${driver.is_closed ? 'closed' : ''}`}>
                    <span>{getWindowStatusText(driver)}</span>
                    <button
                      onClick={() => forceConfirmStart(driver.driver_id, driver.name)}
                      className="force-confirm-btn"
                    >
                      강제 배송시작
                    </button>
                  </div>
                )}

                <div className="apartments">
                  <div className="apartments-header">
                    <strong>담당 아파트:</strong>
                    <button
                      type="button"
                      className="assignment-edit-btn"
                      onClick={() => startAssignmentEdit(driver)}
                    >
                      배정 수정
                    </button>
                  </div>
                  {driver.has_delivery && (
                    <p className="driver-delivery-count">
                      배송 차트 {driver.delivery_apartment_count || 0}개 아파트 / 총 {driver.delivery_total_count || 0}건
                    </p>
                  )}
                  {driver.assigned_apartments.length > 0 ? (
                    <ul>
                      {driver.assigned_apartments.map(apt => (
                        <li key={apt}>
                          {apt}
                          {driver.delivery_apartments?.includes(apt) && <span className="delivery-active-badge">배송 있음</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-assignment">배정 없음</p>
                  )}
                  {assignmentEditingDriverId === driver.driver_id && (
                    <div className="assignment-editor">
                      <div className="assignment-editor-top">
                        <span>선택 {assignmentSelections[driver.driver_id]?.length || 0}개</span>
                        <button
                          type="button"
                          className="assignment-clear-btn"
                          onClick={() => setAssignmentSelections(prev => ({
                            ...prev,
                            [driver.driver_id]: [],
                          }))}
                          disabled={assignmentSavingDriverId === driver.driver_id}
                        >
                          전체 해제
                        </button>
                      </div>
                      {apartmentNamesLoading && allApartmentNames.length === 0 ? (
                        <p className="assignment-loading">아파트 목록을 불러오는 중...</p>
                      ) : allApartmentNames.length > 0 ? (
                        <div className="assignment-options">
                          {allApartmentNames.map(aptName => {
                            const selected = (assignmentSelections[driver.driver_id] || []).includes(aptName);
                            return (
                              <label
                                key={aptName}
                                className={`assignment-option ${selected ? 'selected' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleDriverApartmentAssignment(driver.driver_id, aptName)}
                                  disabled={assignmentSavingDriverId === driver.driver_id}
                                />
                                <span>{aptName}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="assignment-loading">등록된 아파트 목록이 없습니다.</p>
                      )}
                      <div className="assignment-actions">
                        <button
                          type="button"
                          className="assignment-cancel-btn"
                          onClick={() => cancelAssignmentEdit(driver.driver_id)}
                          disabled={assignmentSavingDriverId === driver.driver_id}
                        >
                          취소
                        </button>
                        <button
                          type="button"
                          className="assignment-save-btn"
                          onClick={() => saveDriverAssignments(driver)}
                          disabled={assignmentSavingDriverId === driver.driver_id}
                        >
                          {assignmentSavingDriverId === driver.driver_id ? '저장 중...' : '저장'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {driver.confirmed && driver.confirmation_time && (
                  <div className="confirmation-info-wrapper">
                    <div className="confirmation-info">
                      <strong>확인 시각:</strong>
                      <p>{new Date(driver.confirmation_time).toLocaleString('ko-KR')}</p>
                    </div>
                    <button
                      onClick={() => resetConfirmation(driver.driver_id, driver.name)}
                      className="reset-btn"
                      title="배송 시작 확인 초기화"
                    >
                      🔄 초기화
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 사진 설정 모달 */}
      {showPhotoSettings && (
        <div className="settings-modal" onClick={() => setShowPhotoSettings(false)}>
          <div className="modal-content photo-settings-content" onClick={(e) => e.stopPropagation()}>
            <h2>📷 배송 사진 촬영 설정</h2>
            
            {photoSettingsLoading ? (
              <div className="loading">로딩 중...</div>
            ) : (
              <>
                <div className="form-group">
                  <label className="photo-toggle-label">
                    <input
                      type="checkbox"
                      checked={photoGlobalRequired}
                      onChange={(e) => setPhotoGlobalRequired(e.target.checked)}
                      style={{ width: '20px', height: '20px', marginRight: '10px' }}
                    />
                    <span style={{ fontWeight: 600 }}>사진 촬영 필수 (모든 아파트)</span>
                  </label>
                  <p className="help-text">
                    활성화하면 배송 완료 전에 사진 촬영이 필수입니다.
                    비활성화 시 사진 촬영은 선택사항이 됩니다.
                  </p>
                </div>

                <div className="form-group">
                  <label>사진 보관 기간</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      min={1}
                      max={30}
                      value={photoRetentionDays}
                      onChange={(e) => setPhotoRetentionDays(parseInt(e.target.value, 10) || 5)}
                      className="text-input"
                      style={{ width: '80px', textAlign: 'center' }}
                    />
                    <span>일 후 자동 삭제</span>
                  </div>
                  <p className="help-text">
                    저장 시 S3 자동 삭제 정책과 함께 동기화됩니다. 기본 권장값은 5일입니다.
                  </p>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>아파트별 사진 필수 설정</label>
                  <p className="help-text" style={{ marginBottom: '10px' }}>
                    전체 설정과 별개로, 특정 아파트만 사진 필수로 지정할 수 있습니다.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {allApartmentNames.length > 0 ? (
                      allApartmentNames.map(aptName => {
                        const aptSetting = photoRequiredApartments.find(a => a.apartment_name === aptName);
                        const isRequired = aptSetting?.photo_required || false;
                        return (
                          <label
                            key={aptName}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 12px',
                              background: isRequired ? '#e3f2fd' : '#f5f5f5',
                              border: `1px solid ${isRequired ? '#2196F3' : '#e0e0e0'}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: isRequired ? 600 : 400,
                              color: isRequired ? '#1565C0' : '#333',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isRequired}
                              onChange={() => toggleApartmentPhotoRequired(aptName)}
                              style={{ width: '16px', height: '16px' }}
                            />
                            {aptName}
                          </label>
                        );
                      })
                    ) : (
                      <p style={{ color: '#999', fontSize: '13px' }}>등록된 배송 차트가 없습니다.</p>
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button onClick={() => setShowPhotoSettings(false)} className="cancel-btn">
                    취소
                  </button>
                  <button onClick={savePhotoSettings} className="save-btn">
                    저장
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryStartAdmin;
